import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FormTemplateSummary } from '@foundation/shared';
import { FORM_SCOPES, FORM_SCOPE_LABEL_TH } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Empty, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

const STATUS_TH: Record<string, string> = {
  draft: 'ฉบับร่าง',
  published: 'เผยแพร่แล้ว',
  archived: 'เก็บถาวร',
};

const STATUS_TONE: Record<string, string> = {
  draft: 'amber',
  published: 'green',
  archived: '',
};

export default function FormList() {
  const { allowed } = useAuth();
  const [rows, setRows] = useState<FormTemplateSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [scope, setScope] = useState('');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api<{ data: FormTemplateSummary[] }>(
        `/forms${buildQuery({ scope, status })}`,
      );
      setRows(res.data);
    } catch (e) {
      setError(e);
      setRows([]);
    }
  }, [scope, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>แบบสำรวจ</h1>
          <p>
            แก้ไขคำถามได้เองโดยไม่ต้องแก้โค้ด — แบบฟอร์มที่เผยแพร่แล้วแก้ไม่ได้
            ต้องสร้างเวอร์ชันใหม่ เพื่อให้ข้อมูลเก่ายังอ่านออกเสมอ
          </p>
        </div>
        {allowed('form:write') && <button onClick={() => setShowCreate(true)}>+ สร้างแบบฟอร์ม</button>}
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field">
            <label htmlFor="scope">ประเภท</label>
            <select id="scope" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {FORM_SCOPES.map((s) => (
                <option key={s} value={s}>
                  {FORM_SCOPE_LABEL_TH[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="status">สถานะ</label>
            <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {Object.entries(STATUS_TH).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>ยังไม่มีแบบฟอร์มตามเงื่อนไขนี้</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>เวอร์ชัน</th>
                  <th className="wrap">ชื่อ</th>
                  <th>ประเภท</th>
                  <th>สถานะ</th>
                  <th>เผยแพร่เมื่อ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td className="mono">{f.code}</td>
                    <td>v{f.version}</td>
                    <td className="wrap">
                      <Link className="row-link" to={`/forms/${f.id}`}>
                        {f.name}
                      </Link>
                    </td>
                    <td>{FORM_SCOPE_LABEL_TH[f.scope as keyof typeof FORM_SCOPE_LABEL_TH] ?? f.scope}</td>
                    <td>
                      <span className={`badge ${STATUS_TONE[f.status] ?? ''}`}>
                        {STATUS_TH[f.status] ?? f.status}
                      </span>
                    </td>
                    <td className="small muted">{formatDateTime(f.publishedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateFormModal onClose={() => setShowCreate(false)} />}
    </>
  );
}

const STARTER_SCHEMA = {
  version: 1,
  sections: [
    {
      key: 'main',
      title: 'ส่วนหลัก',
      fields: [{ key: 'note', type: 'textarea', label: 'บันทึกของเจ้าหน้าที่' }],
    },
  ],
};

function CreateFormModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<string>('followup');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<{ id: string }>('/forms', {
        method: 'POST',
        body: { code: code.trim().toUpperCase(), name: name.trim(), scope, schema: STARTER_SCHEMA },
      });
      navigate(`/forms/${res.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="สร้างแบบฟอร์มใหม่" onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorAlert error={error} />
        <div className="field">
          <label htmlFor="code">รหัสแบบฟอร์ม *</label>
          <input
            id="code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="INTAKE_ELDERLY"
          />
          <div className="hint">
            ตัวพิมพ์ใหญ่ ตัวเลข และ _ เท่านั้น — ใช้อ้างถึงแบบฟอร์มข้ามเวอร์ชัน เปลี่ยนภายหลังไม่ได้
          </div>
        </div>
        <div className="field">
          <label htmlFor="name">ชื่อแบบฟอร์ม *</label>
          <input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="newScope">ประเภท *</label>
          <select id="newScope" value={scope} onChange={(e) => setScope(e.target.value)}>
            {FORM_SCOPES.map((s) => (
              <option key={s} value={s}>
                {FORM_SCOPE_LABEL_TH[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'กำลังสร้าง…' : 'สร้างและแก้ไขต่อ'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
